// Lógica de un apartado de descriptores del programa de asignatura
// (MatterProgramDescriptors). Apartados por page_position, en orden visual:
// 1 = arriba, 2 = entre los RA y las evaluaciones, 3 = después de las
// evaluaciones, 4 = después de los recursos del ambiente de aprendizaje,
// 5 = al final.
//
// Según matter.use_matter_values el contenido se guarda por plan
// (EPMMiddleDescriptor) o en la asignatura base (MMiddleDescriptor); sin
// matter (OldMatterProgram) siempre se usa la asignatura base (matter_id).
// El orden es relativo a cada apartado, así reordenar uno no toca los demás.
import { computed, getCurrentInstance } from "vue";
import * as names from "@/store/names";
import { toast } from "@/utils/utils";

export default function useMatterProgramDescriptors(props, emit) {
  const proxy = getCurrentInstance().proxy;
  const store = proxy.$store;

  const useEpmValues = computed(
    () => !!props.matter && !props.matter.use_matter_values
  );
  const baseMatterId = computed(() =>
    props.matter ? props.matter.matter : props.matter_id
  );
  const refComponent = computed(
    () => `${useEpmValues.value ? "EPMatter" : "Matter"}${props.page_position}`
  );
  const canEdit = computed(() => {
    const owner = useEpmValues.value ? props.matter : props.oldMatter;
    return props.allows_crud && !!owner && !owner.is_closed;
  });

  const pageTitles = computed(() =>
    props.descriptor_titles.filter(
      (x) => x.page_position == props.page_position
    )
  );
  const pageTitleIds = computed(() => pageTitles.value.map((x) => x.id));

  const middleDescriptors = computed(() => {
    if (useEpmValues.value)
      return store.getters[names.EPM_MIDDLE_DESCRIPTORS].filter(
        (x) => x.egress_profile_matter == props.egress_profile_matter_id
      );
    if (!baseMatterId.value) return [];
    return store.getters[names.M_MIDDLE_DESCRIPTORS].filter(
      (x) => x.matter == baseMatterId.value
    );
  });
  const pageDescriptors = computed(() =>
    middleDescriptors.value.filter((x) =>
      pageTitleIds.value.includes(x.descriptor)
    )
  );
  const nextOrder = computed(
    () =>
      pageDescriptors.value.reduce((max, x) => Math.max(max, x.order), 0) + 1
  );
  const patchAction = computed(() =>
    useEpmValues.value
      ? names.PATCH_EPM_MIDDLE_DESCRIPTORS
      : names.PATCH_M_MIDDLE_DESCRIPTORS
  );

  function createTitle(title) {
    if (!isNaN(title.id)) return;
    return store
      .dispatch(names.POST_EGRESS_PROFILE_MATTER_DESCRIPTORS, title)
      .then((response) => {
        // El padre lo marca como borrable sin esperar al endpoint deletable
        emit("titleCreated", response);
        createDescription("", response);
      });
  }

  function updateTitle(title) {
    if (isNaN(title.id)) return;
    store.dispatch(names.PATCH_EGRESS_PROFILE_MATTER_DESCRIPTORS, {
      id: title.id,
      item: {
        title: title.title,
        is_default: title.is_default,
      },
    });
  }

  function createDescription(text, title) {
    if (isNaN(title.id)) return;
    if (useEpmValues.value) {
      if (!props.egress_profile_matter_id) return;
      store.dispatch(names.POST_EPM_MIDDLE_DESCRIPTORS, {
        egress_profile_matter: props.egress_profile_matter_id,
        descriptor: title.id,
        description: text,
        order: nextOrder.value,
      });
    } else {
      if (!baseMatterId.value) return;
      store.dispatch(names.POST_M_MIDDLE_DESCRIPTORS, {
        matter: baseMatterId.value,
        descriptor: title.id,
        description: text,
        order: nextOrder.value,
      });
    }
    toast(
      String(proxy.$t("matterProgram.matterProgramView.descriptorCreated"))
    );
  }

  function updateDescription(middle_descriptor, text) {
    if (isNaN(middle_descriptor.id)) return;
    store.dispatch(patchAction.value, {
      id: middle_descriptor.id,
      item: { description: text },
    });
    toast(
      String(proxy.$t("matterProgram.matterProgramView.descriptorUpdated"))
    );
  }

  function updateVisible(middle_descriptor) {
    if (isNaN(middle_descriptor.id)) return;
    store.dispatch(patchAction.value, {
      id: middle_descriptor.id,
      item: { is_visible: !middle_descriptor.is_visible },
    });
  }

  function updateOrder(list) {
    const ids = middleDescriptors.value.map((x) => x.id);
    list.forEach((element) => {
      if (isNaN(element.id) || !ids.includes(element.id)) return;
      store.dispatch(patchAction.value, {
        id: element.id,
        item: { order: element.order },
      });
    });
  }

  // Contenido arrastrado desde otro apartado. El apartado es del título
  // (compartido por todas las asignaturas), así que se confirma y se mueve el
  // título; luego se reordena este apartado con el contenido en su lugar.
  function moveToSection(middle_descriptor, list) {
    const title = props.descriptor_titles.find(
      (x) => x.id == middle_descriptor.descriptor
    );
    if (!title || isNaN(middle_descriptor.id)) return;
    // Los títulos de sistema (internal_use_id) tienen su apartado fijo
    if (title.internal_use_id != null) {
      return proxy.$swal({
        title: proxy.$t("matterProgram.matterProgramView.moveSectionLocked"),
        type: "info",
      });
    }
    return proxy
      .$swal({
        title: proxy.$t("matterProgram.matterProgramView.moveSectionConfirm"),
        text: proxy.$t("matterProgram.matterProgramView.moveSectionWarning", {
          entity: proxy.$getVisibleNames(
            "mesh.egressprofilematter",
            true,
            "Asignaturas"
          ),
        }),
        type: "warning",
        showCancelButton: true,
        cancelButtonText: proxy.$t("reusable.common.cancel"),
      })
      .then((result) => {
        if (!result.value) return;
        return store
          .dispatch(names.PATCH_EGRESS_PROFILE_MATTER_DESCRIPTORS, {
            id: title.id,
            item: { page_position: props.page_position },
          })
          .then(() => {
            const changed = [];
            list.forEach((element, index) => {
              if (element.order == index + 1) return;
              element.order = index + 1;
              changed.push(element);
            });
            updateOrder(changed);
          });
      });
  }

  function deleteDescription(middle_descriptor) {
    return proxy
      .$swal({
        title: proxy.$t("matterProgram.matterProgramView.deleteContentConfirm"),
        text: `"${proxy.$t(
          "matterProgram.matterProgramView.affectOthersWarning"
        )} ${proxy.$getVisibleNames(
          "mesh.egressprofilematter",
          true,
          "Asignaturas"
        )}."`,
        type: "warning",
        showCancelButton: true,
        cancelButtonText: proxy.$t("reusable.common.cancel"),
      })
      .then((result) => {
        if (!result.value) return;
        store.dispatch(
          useEpmValues.value
            ? names.DELETE_EPM_MIDDLE_DESCRIPTORS
            : names.DELETE_M_MIDDLE_DESCRIPTORS,
          middle_descriptor.id
        );
      });
  }

  return {
    useEpmValues,
    refComponent,
    canEdit,
    pageTitles,
    pageDescriptors,
    createTitle,
    updateTitle,
    createDescription,
    updateDescription,
    updateVisible,
    updateOrder,
    moveToSection,
    deleteDescription,
  };
}
